import { useEffect, useState } from "react";
import PageLayout from "../components/PageLayout";
import RoundBox from "../components/RoundBox";
import { buttonBackgroundColor, buttonStyle, inputStyle } from "../components/Style";
import { apiClient } from "../services/api";

type UserProfile = {
  id: number;
  username: string;
  role: "admin" | "teacher" | "viewer";
  photo: string | null;
  hasGoogle: boolean;
};

function ProfilePage() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const clearMessages = () => {
    setSuccessMessage("");
    setErrorMessage("");
  };

  const loadProfile = async () => {
    setLoading(true);
    clearMessages();
    try {
      const data = await apiClient.getProfile();
      setProfile(data);
      setPhotoPreview(data.photo || null);
    } catch (err) {
      console.error("프로필 불러오기 실패", err);
      setErrorMessage("프로필을 불러올 수 없습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setPhotoPreview(result);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadPhoto = async () => {
    if (!photoPreview) return;
    clearMessages();
    try {
      await apiClient.uploadPhoto(photoPreview);
      setSuccessMessage("사진을 업로드했습니다.");
      setProfile((prev) => (prev ? { ...prev, photo: photoPreview } : prev));
    } catch (err) {
      console.error("사진 업로드 실패", err);
      setErrorMessage("사진 업로드에 실패했습니다.");
    }
  };

  const handleRemovePhoto = async () => {
    clearMessages();
    try {
      await apiClient.removePhoto();
      setPhotoPreview(null);
      setProfile((prev) => (prev ? { ...prev, photo: null } : prev));
      setSuccessMessage("사진을 제거했습니다.");
    } catch (err) {
      console.error("사진 제거 실패", err);
      setErrorMessage("사진 제거에 실패했습니다.");
    }
  };

  const handleChangePassword = async () => {
    clearMessages();
    if (!currentPassword || !newPassword || !confirmPassword) {
      setErrorMessage("모든 비밀번호 입력란을 채워주세요.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage("새 비밀번호가 일치하지 않습니다.");
      return;
    }
    try {
      await apiClient.changePassword(currentPassword, newPassword);
      setSuccessMessage("비밀번호를 변경했습니다.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error("비밀번호 변경 실패", err);
      setErrorMessage("비밀번호 변경에 실패했습니다.");
    }
  };

  return (
    <PageLayout title="프로필">
      {successMessage && (
        <div
          style={{
            backgroundColor: "#efe",
            color: "#060",
            padding: "12px",
            borderRadius: "6px",
            marginBottom: "20px",
            fontWeight: "500",
          }}
        >
          ✓ {successMessage}
        </div>
      )}

      {errorMessage && (
        <div
          style={{
            backgroundColor: "#fee",
            color: "#d00",
            padding: "12px",
            borderRadius: "6px",
            marginBottom: "20px",
            fontWeight: "500",
          }}
        >
          ✗ {errorMessage}
        </div>
      )}

      {loading ? (
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>로딩 중...</p>
        </div>
      ) : !profile ? (
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p style={{ color: "#d00" }}>프로필을 불러올 수 없습니다.</p>
        </div>
      ) : (
        <>
          <RoundBox label="기본 정보">
            <div>
              <div style={{ marginBottom: "15px" }}>
                <label
                  style={{
                    fontWeight: "bold",
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "13px",
                    color: "#666",
                  }}
                >
                  아이디
                </label>
                <div
                  style={{
                    padding: "10px",
                    backgroundColor: "#fff",
                    borderRadius: "6px",
                    fontWeight: "500",
                    border: "1px solid #ddd",
                  }}
                >
                  {profile.username}
                </div>
              </div>
              <div>
                <label
                  style={{
                    fontWeight: "bold",
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "13px",
                    color: "#666",
                  }}
                >
                  권한
                </label>
                <div
                  style={{
                    display: "inline-block",
                    padding: "8px 16px",
                    backgroundColor:
                      profile.role === "admin"
                        ? "#ffd700"
                        : profile.role === "teacher"
                        ? "#87ceeb"
                        : "#e0e0e0",
                    borderRadius: "6px",
                    fontWeight: "bold",
                    fontSize: "14px",
                  }}
                >
                  {profile.role === "admin" ? "관리자" : profile.role === "teacher" ? "선생님" : "뷰어"}
                </div>
              </div>
            </div>
          </RoundBox>

          <RoundBox label="프로필 사진">
            <div>
              <div style={{ marginBottom: "20px", textAlign: "center" }}>
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="프로필 사진"
                    style={{
                      width: "100px",
                      height: "100px",
                      borderRadius: "50%",
                      border: "3px solid #ddd",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "100px",
                      height: "100px",
                      borderRadius: "50%",
                      backgroundColor: "#f0f0f0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "48px",
                      margin: "0 auto",
                      border: "2px solid #ddd",
                    }}
                  >
                    📷
                  </div>
                )}
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label
                  style={{
                    fontWeight: "bold",
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "13px",
                    color: "#666",
                  }}
                >
                  이미지 파일 선택
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  style={{
                    display: "block",
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                />
                <p
                  style={{
                    fontSize: "12px",
                    color: "#999",
                    margin: "5px 0 0 0",
                  }}
                >
                  최대 5MB, JPG/PNG 지원
                </p>
              </div>

              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  onClick={handleUploadPhoto}
                  disabled={!photoPreview}
                  style={{
                    ...buttonStyle,
                    ...buttonBackgroundColor.save,
                    flex: 1,
                    opacity: photoPreview ? 1 : 0.5,
                    cursor: photoPreview ? "pointer" : "not-allowed",
                  }}
                >
                  사진 업로드
                </button>
                {profile.photo && (
                  <button
                    onClick={handleRemovePhoto}
                    style={{
                      ...buttonStyle,
                      ...buttonBackgroundColor.delete,
                      flex: 1,
                    }}
                  >
                    사진 제거
                  </button>
                )}
              </div>
            </div>
          </RoundBox>

          <RoundBox label="비밀번호 변경">
            <div>
              <div style={{ marginBottom: "15px" }}>
                <label
                  style={{
                    fontWeight: "bold",
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "13px",
                    color: "#666",
                  }}
                >
                  현재 비밀번호
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="현재 비밀번호"
                  style={{
                    ...inputStyle,
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label
                  style={{
                    fontWeight: "bold",
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "13px",
                    color: "#666",
                  }}
                >
                  새 비밀번호
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="새 비밀번호"
                  style={{
                    ...inputStyle,
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    fontWeight: "bold",
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "13px",
                    color: "#666",
                  }}
                >
                  비밀번호 확인
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="새 비밀번호 확인"
                  style={{
                    ...inputStyle,
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <button
                onClick={handleChangePassword}
                style={{
                  ...buttonStyle,
                  ...buttonBackgroundColor.save,
                  width: "100%",
                }}
              >
                비밀번호 변경
              </button>
            </div>
          </RoundBox>

          <RoundBox label="Google 로그인">
            <div>
              <div
                style={{
                  marginBottom: "20px",
                  padding: "15px",
                  backgroundColor: profile.hasGoogle ? "#efe" : "#fee",
                  borderRadius: "6px",
                  textAlign: "center",
                }}
              >
                {profile.hasGoogle ? (
                  <div>
                    <div
                      style={{
                        fontWeight: "bold",
                        color: "#060",
                        fontSize: "14px",
                      }}
                    >
                      ✓ Google 로그인 연동됨
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      fontWeight: "bold",
                      color: "#d00",
                      fontSize: "14px",
                    }}
                  >
                    ✗ Google 로그인 미연동
                  </div>
                )}
              </div>

              <button
                onClick={() => {
                  const token = localStorage.getItem("jwtToken");
                  const stateParam = token ? `?state=${encodeURIComponent(token)}` : "";
                  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
                  window.location.href = `${apiUrl}/api/auth/google${stateParam}`;
                }}
                style={{
                  ...buttonStyle,
                  ...buttonBackgroundColor.save,
                  width: "100%",
                }}
              >
                {profile.hasGoogle ? "Google 계정 변경/재연동" : "Google 계정 연동"}
              </button>
            </div>
          </RoundBox>
        </>
      )}
    </PageLayout>
  );
}

export default ProfilePage;
